// FormRef: how lessons, exercises and stories point at a Prakrit form without typing it by hand.
// Every reference is resolved by the engine at render time, so content never drifts from the tables.

import type {
    Case,
    Cell,
    FormStatus,
    KridantaKind,
    Num,
    Person,
    Tense
} from '@/prakrit/lib/grammar/types'

import { getNoun, getVerb } from '@/prakrit/lib/grammar/build'
import { PRONOUNS, pronounMeta } from '@/prakrit/lib/grammar/pronouns'
import { AS_DHATU } from '@/prakrit/lib/grammar/verbs'
import { AVYAYA, NUMBERS_LIST, EGA_FORMS, avyayaSurfaces } from '@/prakrit/lib/grammar/lexicon'

export type FormRef =
    | { kind: 'root', root: string }
    | { kind: 'stem', stem: string }
    | { kind: 'verb', root: string, tense: Tense, person: Person, number: Num }
    | { kind: 'kridanta', root: string, type: KridantaKind }
    | { kind: 'as', tense: 'vartaman' | 'bhoot', person: Person, number: Num }
    | { kind: 'noun', stem: string, case: Case, number: Num }
    | { kind: 'pronoun', table: string, case: Exclude<Case, 'sambodhan'>, number: Num }
    | { kind: 'avyaya', word: string }
    | { kind: 'number', word: string }
    | { kind: 'lit', text: string, note?: string }

export interface ResolvedForm {
    form: string
    status: FormStatus
    key: string
    rule?: string
}

export interface Resolved {
    ok: boolean
    /** The single form taught first — what learners are asked for. */
    display: string
    core: ResolvedForm[]
    alt: ResolvedForm[]
    /** All forms valid for this slot (core + alt) — used to reject distractors. */
    valid: string[]
    error?: string
}

const fromCell = (cell: Cell | undefined, keyOf: (form: string) => string): Resolved => {
    if (!cell || cell.length === 0) return { ok: false, display: '', core: [], alt: [], valid: [], error: 'missing cell' }

    const all = cell.map(f => ({ form: f.form, status: f.status, key: keyOf(f.form), rule: f.rule }))
    const core = all.filter(f => f.status === 'core')
    const alt = all.filter(f => f.status === 'alt')

    return {
        ok: core.length > 0,
        display: core[0]?.form ?? '',
        core,
        alt,
        valid: [...core, ...alt].map(f => f.form)
    }
}

const fail = (error: string): Resolved => ({ ok: false, display: '', core: [], alt: [], valid: [], error })

export const resolveRef = (ref: FormRef): Resolved => {
    switch (ref.kind) {
        case 'root': {
            const v = ref.root === 'अस' ? { root: 'अस' } : getVerb(ref.root)

            if (!v) return fail(`unknown root ${ref.root}`)

            const r: ResolvedForm = { form: v.root, status: 'core', key: `lex|dhatu|${v.root}` }

            return { ok: true, display: v.root, core: [r], alt: [], valid: [v.root] }
        }
        case 'stem': {
            const n = getNoun(ref.stem)

            if (!n) return fail(`unknown stem ${ref.stem}`)

            const r: ResolvedForm = { form: n.stem, status: 'core', key: `lex|noun|${n.stem}` }

            return { ok: true, display: n.stem, core: [r], alt: [], valid: [n.stem] }
        }
        case 'verb': {
            const v = getVerb(ref.root)

            if (!v) return fail(`unknown root ${ref.root}`)

            return fromCell(v.forms[ref.tense]?.[ref.person]?.[ref.number], f => `vf|${v.root}|${ref.tense}|${ref.person}|${ref.number}|${f}`)
        }
        case 'kridanta': {
            const v = getVerb(ref.root)

            if (!v) return fail(`unknown root ${ref.root}`)

            return fromCell(v.forms.kridanta[ref.type], f => `kf|${v.root}|${ref.type}|${f}`)
        }
        case 'as':
            return fromCell(AS_DHATU[ref.tense][ref.person][ref.number], f => `vf|अस|${ref.tense}|${ref.person}|${ref.number}|${f}`)
        case 'noun': {
            const n = getNoun(ref.stem)

            if (!n) return fail(`unknown stem ${ref.stem}`)

            return fromCell(n.forms[ref.case]?.[ref.number], f => `nf|${n.stem}|${ref.case}|${ref.number}|${f}`)
        }
        case 'pronoun': {
            const meta = pronounMeta(ref.table)

            if (!meta) return fail(`unknown pronoun ${ref.table}`)

            return fromCell(PRONOUNS[meta.key][ref.case]?.[ref.number], f => `pf|${meta.key}|${ref.case}|${ref.number}|${f}`)
        }
        case 'avyaya': {
            const entry = AVYAYA.find(([w]) => w === ref.word || avyayaSurfaces(w).includes(ref.word))

            if (!entry) return fail(`unknown avyaya ${ref.word}`)

            const form = avyayaSurfaces(entry[0]).includes(ref.word) ? ref.word : avyayaSurfaces(entry[0])[0]
            const r: ResolvedForm = { form, status: 'core', key: `lex|avyaya|${entry[0]}` }

            return { ok: true, display: form, core: [r], alt: [], valid: [form] }
        }
        case 'number': {
            const known = NUMBERS_LIST.some(([w]) => w === ref.word) || EGA_FORMS.some(e => e.form === ref.word)

            if (!known) return fail(`unknown number ${ref.word}`)

            const r: ResolvedForm = { form: ref.word, status: 'core', key: `lex|number|${ref.word}` }

            return { ok: true, display: ref.word, core: [r], alt: [], valid: [ref.word] }
        }
        case 'lit': {
            const r: ResolvedForm = { form: ref.text, status: 'core', key: `lit|${ref.text}` }

            return { ok: true, display: ref.text, core: [r], alt: [], valid: [ref.text] }
        }
    }
}

/** The form a learner sees for this reference. Throws if the engine cannot resolve it (a content bug). */
export const formOf = (ref: FormRef): string => {
    const r = resolveRef(ref)

    if (!r.ok) throw new Error(`Unresolvable FormRef ${JSON.stringify(ref)}: ${r.error}`)

    return r.display
}

/** Short constructors used by lesson and story data. */
export const ROOT = (root: string): FormRef => ({ kind: 'root', root })
export const STEM = (stem: string): FormRef => ({ kind: 'stem', stem })
export const V =(root: string, tense: Tense, person: Person, number: Num): FormRef => ({ kind: 'verb', root, tense, person, number })
export const K = (root: string, type: KridantaKind): FormRef => ({ kind: 'kridanta', root, type })
export const AS = (tense: 'vartaman' | 'bhoot', person: Person, number: Num): FormRef => ({ kind: 'as', tense, person, number })
export const N = (stem: string, c: Case, number: Num): FormRef => ({ kind: 'noun', stem, case: c, number })
export const P = (table: string, c: Exclude<Case, 'sambodhan'>, number: Num): FormRef => ({ kind: 'pronoun', table, case: c, number })
export const AV = (word: string): FormRef => ({ kind: 'avyaya', word })
export const NUM = (word: string): FormRef => ({ kind: 'number', word })
export const LIT = (text: string, note?: string): FormRef => ({ kind: 'lit', text, note })

/** The subject pronoun for a person/number in प्रथमा (अहं, अम्हे, तुमं, तुम्हे, सो, ते, सा, ताओ …). */
export const SUBJ = (person: Person, number: Num, gender: 'pu' | 'stri' = 'pu'): FormRef => {
    if (person === 'uttam') return P('amha', 'prathama', number)
    if (person === 'madhyam') return P('tumha', 'prathama', number)

    return P(gender === 'stri' ? 'ta-stri' : 'ta-pu', 'prathama', number)
}
